import React, { useState, useEffect } from 'react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useTheme } from '../App';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';

const Dashboard = () => {
  const { darkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useUser();
  const isRTL = i18n.language === 'ar';
  
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set page title
  useEffect(() => {
    document.title = 'Dashboard - Idealyze';
    return () => {
      document.title = 'Idealyze';
    };
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Always try to fetch real data first, regardless of authentication status
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          console.log('Attempting to fetch real data from API...');
          console.log('API URL:', process.env.NODE_ENV === 'production' 
            ? process.env.REACT_APP_API_URL_PRODUCTION 
            : (process.env.REACT_APP_API_URL || 'https://idealyze-backend.vercel.app'));
          // Token validation check
          
          const data = await apiClient.getUserAnalytics(token);
          // Analytics data retrieved successfully
          
          // Use the data from the backend API as-is (it already correctly counts build clicks)
          setDashboardData(data);
          setError(null);
          return;
        } catch (err) {
          console.error('API Error - falling back to direct Supabase queries:', err);
          console.log('Attempting direct Supabase fallback...');
          
          // Try direct Supabase queries as fallback
          try {
            const directData = await fetchDirectSupabaseData();
            console.log('Direct Supabase data received:', directData);
            setDashboardData(directData);
            setError(null);
            return;
          } catch (supabaseErr) {
            console.error('Supabase fallback also failed:', supabaseErr);
            setError(`Both API and Supabase failed. API: ${err.message}, Supabase: ${supabaseErr.message}`);
          }
        }
      } else {
        console.log('No token found - trying to fetch basic data directly from Supabase');
        try {
          const directData = await fetchDirectSupabaseData();
          console.log('Direct Supabase data received (no auth):', directData);
          setDashboardData(directData);
          setError(null);
          return;
        } catch (supabaseErr) {
          console.error('Direct Supabase query failed:', supabaseErr);
          setError('No authentication and Supabase query failed');
        }
      }
      
      // If everything fails, show empty data (no fake numbers)
      console.log('Using empty data structure');
      setDashboardData(generateEmptyDashboardData());
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(`Error: ${err.message}`);
      setDashboardData(generateEmptyDashboardData());
    } finally {
      setLoading(false);
    }
  };

  const fetchDirectSupabaseData = async () => {
    // Fetch real data directly from Supabase
    console.log('Fetching data directly from Supabase...');
    
    // Get current user session  
    const { data: { session } } = await supabase.auth.getSession();
    const currentUserId = session?.user?.id;
    
    // Get total ideas count
    const { count: totalIdeas } = await supabase
      .from('ideas')
      .select('*', { count: 'exact', head: true });
    
    console.log('Total ideas from DB:', totalIdeas);
    
    let userInteractions = [];
    let totalViews = 0;
    let bookmarkedCount = 0;
    let exploredCount = 0;
    let startedBuildingCount = 0;
    
    if (currentUserId) {
      console.log('User authenticated, fetching user-specific data...');
      
      // Get user interactions
      const { data: interactions, error: interactionsError } = await supabase
        .from('user_idea_interactions')
        .select('*')
        .eq('user_id', currentUserId);
      
      if (interactionsError) {
        console.error('Error fetching user interactions:', interactionsError);
      } else {
        userInteractions = interactions || [];
        // User interactions retrieved
        
        totalViews = userInteractions.filter(i => i.viewed_at).length;
        bookmarkedCount = userInteractions.filter(i => i.is_bookmarked).length;
        exploredCount = userInteractions.filter(i => i.viewed_at).length;
        
        // Count rows where build_clicked_at is not null
        const { count: buildClicksCount } = await supabase
          .from('user_idea_interactions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', currentUserId)
          .not('build_clicked_at', 'is', null);
          
        startedBuildingCount = buildClicksCount || 0;
        console.log('✅ Started Building Count for user:', startedBuildingCount);
      }
    } else {
      console.log('No authenticated user, showing public data only');
    }
    
    // Get global popular industry and type
    const { data: allInteractions } = await supabase
      .from('user_idea_interactions')
      .select(`
        ideas!inner (industry, type)
      `)
      .not('viewed_at', 'is', null);
    
    const industryCount = {};
    const typeCount = {};
    
    (allInteractions || []).forEach(interaction => {
      const industry = interaction.ideas?.industry;
      const type = interaction.ideas?.type;
      
      if (industry) industryCount[industry] = (industryCount[industry] || 0) + 1;
      if (type) typeCount[type] = (typeCount[type] || 0) + 1;
    });
    
    const topIndustry = Object.keys(industryCount).reduce((a, b) => 
      industryCount[a] > industryCount[b] ? a : b, 'E-commerce');
    const topType = Object.keys(typeCount).reduce((a, b) => 
      typeCount[a] > typeCount[b] ? a : b, 'SaaS');
    
    console.log('Industry counts:', industryCount);
    console.log('Type counts:', typeCount);
    
    // Generate empty views data (real data would require date-based queries)
    const viewsData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      viewsData.push({
        date: date.toISOString().split('T')[0],
        views: 0, // Would need complex date-based query
        uniqueViews: 0
      });
    }
    
    // Generate empty heatmap data
    const heatmapData = [];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const categories = ['SaaS', 'Mobile app', 'E-commerce', 'Platform'];
    
    days.forEach((day, dayIndex) => {
      categories.forEach((category, categoryIndex) => {
        heatmapData.push({
          day,
          category,
          value: 0, // Would need complex date-based query
          dayIndex,
          categoryIndex
        });
      });
    });
    
    return {
      summaryStats: {
        totalIdeas: totalIdeas || 0,
        totalViews,
        averageViewsPerDay: Math.round(totalViews / 7),
        mostPopularIndustry: topIndustry,
        mostPopularType: topType
      },
      industryData: [], // Would need user-specific industry analysis
      typeData: [],
      difficultyData: [],
      viewsData,
      heatmapData,
      progressData: {
        totalIdeas: totalIdeas || 0,
        bookmarked: bookmarkedCount,
        explored: exploredCount,
        startedBuilding: startedBuildingCount
      }
    };
  };

  const generateEmptyDashboardData = () => {
    // Generate ONLY empty/zero data structure - no fake numbers
    const viewsData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      viewsData.push({
        date: date.toISOString().split('T')[0],
        views: 0,  // Always 0 - only real data should show numbers
        uniqueViews: 0
      });
    }

    const heatmapData = [];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const categories = ['SaaS', 'Mobile app', 'E-commerce', 'Platform'];
    
    days.forEach((day, dayIndex) => {
      categories.forEach((category, categoryIndex) => {
        heatmapData.push({
          day,
          category,
          value: 0,  // Always 0 - only real data should show numbers
          dayIndex,
          categoryIndex
        });
      });
    });

    return {
      summaryStats: {
        totalIdeas: 0,  // Will be updated when we can fetch real count
        totalViews: 0,  // Only real database views, not localStorage
        averageViewsPerDay: 0,
        mostPopularIndustry: 'No data yet',
        mostPopularType: 'No data yet'
      },
      industryData: [],
      typeData: [],
      difficultyData: [],
      viewsData,
      heatmapData,
      progressData: {
        totalIdeas: 0,
        bookmarked: 0,  // Only real database bookmarks
        explored: 0,    // Only real database views
        startedBuilding: 0  // Only real AI interactions
      }
    };
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50'
      }`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-32 w-32 border-b-2 ${
            darkMode ? 'border-white' : 'border-indigo-600'
          }`}></div>
          <p className={`mt-4 text-lg ${
            darkMode ? 'text-white' : 'text-gray-700'
          }`}>
            {t('loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50'
      }`}>
        <div className="text-center">
          <p className={`text-lg ${
            darkMode ? 'text-red-400' : 'text-red-600'
          }`}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }
  
  const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];
  
  // Helper function to translate industry names
  const translateIndustry = (industry) => {
    if (!industry || industry === 'No data yet') return t('dashboard.noDataYet');
    
    const industryMap = {
      'E-commerce': t('industries.ecommerce'),
      'Education': t('industries.education'), 
      'Services': t('industries.services'),
      'Fashion': t('industries.fashion'),
      'Productivity': t('industries.productivity'),
      'Entertainment': t('industries.entertainment'),
      'Sports': t('industries.sports'),
      'Shopping': t('industries.shopping'),
      'Social Media': t('industries.socialMedia'),
      'Self-Development': t('industries.selfDevelopment')
    };
    
    return industryMap[industry] || industry;
  };
  
  // Helper function to translate type names  
  const translateType = (type) => {
    if (!type || type === 'No data yet') return t('dashboard.noDataYet');
    
    const typeMap = {
      'SaaS': t('filterOptions.type.SaaS'),
      'Mobile app': t('filterOptions.type.Mobile app'),
      'E-commerce website': t('filterOptions.type.E-commerce website'),
      'Platform': t('filterOptions.type.Platform')
    };
    
    return typeMap[type] || type;
  };
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`px-4 py-3 rounded-xl shadow-2xl border backdrop-blur-sm ${
          darkMode 
            ? 'bg-gray-800/90 border-gray-700 text-white' 
            : 'bg-white/90 border-gray-200 text-gray-800'
        }`}>
          <p className="font-semibold text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs flex items-center gap-2">
              <span 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {`${entry.dataKey}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const StatCard = ({ title, value, change, icon, color = "indigo" }) => (
    <div className={`relative overflow-hidden rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${
      darkMode 
        ? 'bg-gray-800/50 backdrop-blur-sm border border-gray-700 hover:bg-gray-800/70' 
        : 'bg-white/70 backdrop-blur-sm border border-gray-200 hover:bg-white/90 shadow-lg'
    }`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium ${
            darkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {title}
          </p>
          <p className={`text-3xl font-bold mt-1 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {value}
          </p>
          {change && (
            <p className={`text-sm mt-1 ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {change}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-r ${
          color === 'indigo' ? 'from-indigo-500 to-purple-600' :
          color === 'green' ? 'from-green-500 to-emerald-600' :
          color === 'blue' ? 'from-blue-500 to-cyan-600' :
          'from-orange-500 to-red-600'
        }`}>
          {icon}
        </div>
      </div>
      <div className={`absolute -bottom-2 -right-2 w-20 h-20 rounded-full opacity-10 bg-gradient-to-r ${
        color === 'indigo' ? 'from-indigo-500 to-purple-600' :
        color === 'green' ? 'from-green-500 to-emerald-600' :
        color === 'blue' ? 'from-blue-500 to-cyan-600' :
        'from-orange-500 to-red-600'
      }`} />
    </div>
  );

  const ChartCard = ({ title, subtitle, children, className = "" }) => (
    <div className={`rounded-2xl p-6 transition-all duration-300 ${
      darkMode 
        ? 'bg-gray-800/50 backdrop-blur-sm border border-gray-700' 
        : 'bg-white/70 backdrop-blur-sm border border-gray-200 shadow-lg'
    } ${className}`}>
      <div className="mb-6">
        <h3 className={`text-xl font-bold ${
          darkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {title}
        </h3>
        {subtitle && (
          <p className={`text-sm mt-1 ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );

  const EmptyStateMessage = ({ message }) => (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <div className={`w-16 h-16 rounded-full mb-4 flex items-center justify-center ${
        darkMode ? 'bg-gray-700' : 'bg-gray-100'
      }`}>
        <svg className={`w-8 h-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </div>
      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
        {message}
      </p>
      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'} mb-4`}>
        {t('dashboard.startExploringTip')}
      </p>
      <Link 
        to="/ideas-vault"
        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 inline-block text-center no-underline"
      >
        {t('dashboard.exploreIdeas')}
      </Link>
    </div>
  );

  const ProgressBar = ({ label, value, total, color, percentage }) => (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className={`text-sm font-medium ${
          darkMode ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {label}
        </span>
        <span className={`text-sm font-bold ${
          darkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {value} / {total}
        </span>
      </div>
      <div className={`w-full h-4 rounded-full overflow-hidden shadow-inner ${
        darkMode ? 'bg-gray-700' : 'bg-gray-200'
      }`}>
        <div 
          className={`h-full rounded-full transition-all duration-700 ease-out shadow-sm ${color}`}
          style={{ 
            width: `${percentage}%`,
            direction: isRTL ? 'rtl' : 'ltr',
            minWidth: percentage > 0 ? '8px' : '0px'
          }}
        />
      </div>
    </div>
  );

  const HeatmapGrid = ({ data }) => {
    const maxValue = Math.max(...data.map(d => d.value));
    
    // English day names from backend
    const backendDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    // English category names from backend  
    const backendCategories = ['SaaS', 'Mobile app', 'E-commerce', 'Platform'];
    
    // Translated names for display
    const displayDays = [
      t('dashboard.days.sun'),
      t('dashboard.days.mon'),
      t('dashboard.days.tue'),
      t('dashboard.days.wed'),
      t('dashboard.days.thu'),
      t('dashboard.days.fri'),
      t('dashboard.days.sat')
    ];
    const displayCategories = [
      t('dashboard.categories.saas'),
      t('dashboard.categories.mobileApp'),
      t('dashboard.categories.ecommerce'),
      t('dashboard.categories.platform')
    ];
    
    return (
      <div className="space-y-3">
        <div className="flex justify-center items-center gap-3 mb-4">
          <div className="w-12"></div>
          {displayCategories.map(category => (
            <div key={category} className="flex-1 text-center">
              <span className={`text-xs font-medium ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {category}
              </span>
            </div>
          ))}
        </div>
        {displayDays.map((displayDay, dayIndex) => (
          <div key={displayDay} className="flex justify-center items-center gap-3">
            <div className="w-12 text-end">
              <span className={`text-xs font-medium ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {displayDay}
              </span>
            </div>
            {displayCategories.map((displayCategory, categoryIndex) => {
              // Map back to backend names to find data
              const backendDay = backendDays[dayIndex];
              const backendCategory = backendCategories[categoryIndex];
              const cellData = data.find(d => d.day === backendDay && d.category === backendCategory);
              const intensity = cellData ? cellData.value / maxValue : 0;
              return (
                <div
                  key={`${displayDay}-${displayCategory}`}
                  className="flex-1 h-10 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                  style={{ 
                    backgroundColor: `rgba(99, 102, 241, ${Math.max(intensity, 0.1)})`,
                    border: intensity > 0.5 ? '2px solid rgba(99, 102, 241, 0.8)' : 'none'
                  }}
                >
                  <span className="text-xs font-bold text-white drop-shadow-sm">
                    {cellData?.value || 0}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50'
    }`}>
      <div className={`p-6 space-y-8 ${isRTL ? 'rtl' : 'ltr'}`}>
        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title={t('dashboard.totalIdeas')}
            value={dashboardData.summaryStats.totalIdeas}
            icon={<svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            color="indigo"
          />
          <StatCard 
            title={t('dashboard.totalViews')}
            value={dashboardData.summaryStats.totalViews}
            change={`${dashboardData.summaryStats.averageViewsPerDay} ${t('dashboard.avgPerDay')}`}
            icon={<svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>}
            color="green"
          />
          <StatCard 
            title={t('dashboard.topIndustryGlobal')}
            value={translateIndustry(dashboardData.summaryStats.mostPopularIndustry)}
            icon={<svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" /></svg>}
            color="blue"
          />
          <StatCard 
            title={t('dashboard.topTypeGlobal')}
            value={translateType(dashboardData.summaryStats.mostPopularType)}
            icon={<svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>}
            color="orange"
          />
        </div>

        {/* Idea Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Industry Distribution */}
          <ChartCard title={t('dashboard.ideasByIndustry')} subtitle={t('dashboard.yourViewedIdeasByIndustry')}>
            {dashboardData.industryData.length === 0 ? (
              <EmptyStateMessage message={t('dashboard.noIndustryData')} />
            ) : (
              <div style={{ direction: 'ltr' }}>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                  <Pie
                    data={dashboardData.industryData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percentage, value }) => {
                      const translatedName = 
                        name === 'E-commerce' ? t('industries.ecommerce') :
                        name === 'Education' ? t('industries.education') :
                        name === 'Services' ? t('industries.services') :
                        name === 'Fashion' ? t('industries.fashion') :
                        name === 'Productivity' ? t('industries.productivity') :
                        name;
                      return `${percentage ? percentage.toFixed(1) : 0}%`;
                    }}
                    outerRadius={100}
                    innerRadius={30}
                    paddingAngle={2}
                    fill="#8884d8"
                    dataKey="value"
                    labelDistance={isRTL ? 120 : undefined}
                  >
                    {dashboardData.industryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const translatedName = 
                          data.name === 'E-commerce' ? t('industries.ecommerce') :
                          data.name === 'Education' ? t('industries.education') :
                          data.name === 'Services' ? t('industries.services') :
                          data.name === 'Fashion' ? t('industries.fashion') :
                          data.name === 'Productivity' ? t('industries.productivity') :
                          data.name;
                        return (
                          <div className={`px-4 py-3 rounded-xl shadow-2xl border backdrop-blur-sm ${
                            darkMode 
                              ? 'bg-gray-800/90 border-gray-700 text-white' 
                              : 'bg-white/90 border-gray-200 text-gray-800'
                          }`}>
                            <p className="font-semibold text-sm mb-2">{translatedName}</p>
                            <p className="text-xs flex items-center gap-2">
                              <span 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: payload[0].color }}
                              />
                              {`${t('dashboard.value')}: ${data.value} (${data.percentage || 0}%)`}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }} 
                  />
                  <Legend 
                    formatter={(value) => {
                      const translatedName = 
                        value === 'E-commerce' ? t('industries.ecommerce') :
                        value === 'Education' ? t('industries.education') :
                        value === 'Services' ? t('industries.services') :
                        value === 'Fashion' ? t('industries.fashion') :
                        value === 'Productivity' ? t('industries.productivity') :
                        value;
                      return translatedName;
                    }}
                    wrapperStyle={{ direction: isRTL ? 'rtl' : 'ltr', fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              </div>
            )}
          </ChartCard>

          {/* Execution Difficulty */}
          <ChartCard title={t('dashboard.executionDifficulty')} subtitle={t('dashboard.yourViewedIdeasByDifficulty')}>
            {dashboardData.difficultyData.length === 0 ? (
              <EmptyStateMessage message={t('dashboard.noDifficultyData')} />
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={dashboardData.difficultyData} margin={{ 
                  top: 20, 
                  right: isRTL ? 20 : 30, 
                  left: isRTL ? 30 : 20, 
                  bottom: 5 
                }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: darkMode ? '#ffffff' : '#374151', textAnchor: isRTL ? 'end' : 'start' }}
                    tickFormatter={(value) => {
                      // Translate difficulty values for display
                      if (value === 'Easy') return t('difficulty.easy');
                      if (value === 'Moderate') return t('difficulty.moderate');
                      if (value === 'Complex') return t('difficulty.complex');
                      return value;
                    }}
                  />
                  <YAxis 
                    tick={{ fill: darkMode ? '#ffffff' : '#374151' }}
                    orientation={isRTL ? 'right' : 'left'}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[12, 12, 0, 0]}>
                    <defs>
                      <linearGradient id="difficultyGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.9}/>
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.7}/>
                      </linearGradient>
                    </defs>
                    {dashboardData.difficultyData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={
                          entry.name === 'Easy' ? '#10b981' :
                          entry.name === 'Moderate' ? '#f59e0b' :
                          entry.name === 'Complex' ? '#ef4444' :
                          '#8b5cf6'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {/* User Engagement */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Views Over Time */}
          <ChartCard title={t('dashboard.viewsOverTime')} subtitle={t('dashboard.yourViewsLast7Days')}>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart 
                data={dashboardData.viewsData} 
                margin={{ 
                  top: 20, 
                  right: isRTL ? 20 : 30, 
                  left: isRTL ? 30 : 20, 
                  bottom: 5 
                }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                  reversed={isRTL}
                />
                <YAxis orientation={isRTL ? 'right' : 'left'} />
                <Tooltip 
                  content={<CustomTooltip />}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Legend wrapperStyle={{ direction: isRTL ? 'rtl' : 'ltr' }} />
                <Area
                  type="monotone"
                  dataKey="views"
                  stackId="1"
                  stroke="#6366f1"
                  fill="url(#colorViews)"
                  fillOpacity={0.6}
                  name={t('dashboard.dailyViews')}
                />
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Progress Chart */}
          <ChartCard title={t('dashboard.ideaProgress')} subtitle={t('dashboard.yourIdeaJourney')}>
            <div className="space-y-8">
              <ProgressBar
                label={t('dashboard.bookmarked')}
                value={dashboardData.progressData.bookmarked}
                total={dashboardData.progressData.totalIdeas}
                percentage={(dashboardData.progressData.bookmarked / Math.max(dashboardData.progressData.totalIdeas, 1)) * 100}
                color="bg-gradient-to-r from-blue-500 to-blue-600"
              />
              <ProgressBar
                label={t('dashboard.explored')}
                value={dashboardData.progressData.explored}
                total={dashboardData.progressData.totalIdeas}
                percentage={(dashboardData.progressData.explored / Math.max(dashboardData.progressData.totalIdeas, 1)) * 100}
                color="bg-gradient-to-r from-green-500 to-green-600"
              />
              <ProgressBar
                label={t('dashboard.startedBuilding')}
                value={dashboardData.progressData.startedBuilding}
                total={dashboardData.progressData.totalIdeas}
                percentage={(dashboardData.progressData.startedBuilding / Math.max(dashboardData.progressData.totalIdeas, 1)) * 100}
                color="bg-gradient-to-r from-orange-500 to-orange-600"
              />
            </div>
          </ChartCard>
        </div>

        {/* Heatmap */}
        <ChartCard 
          title={t('dashboard.popularCategoriesByDay')}
          subtitle={t('dashboard.yourActivityLast7Days')}
          className="col-span-full"
        >
          {dashboardData.heatmapData.every(item => item.value === 0) ? (
            <EmptyStateMessage message={t('dashboard.noActivityData')} />
          ) : (
            <HeatmapGrid data={dashboardData.heatmapData} />
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default Dashboard;